import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import * as THREE from "three";

/**
 * BreathRibbons (Pustestrømmer / Organiske Lysbånd)
 *
 * A serene, ethereal 3D background experience of 3 undulating silk/light ribbons
 * that breathe and float gently behind the Hero content in harmony with calming
 * 8-second respiratory cycles.
 *
 * Design & Mindfulness Principles:
 * - Fluid & peaceful: Continuous ribbon waves simulating deep, smooth breathing streams.
 * - Soft edge feathering: Procedural gradient texture so ribbons look like light and silk, not sharp polygons.
 * - 8-second breath resonance: Amplitude gently swells and relaxes in sync with deep breaths.
 * - Subtle cursor interactivity: Ribbons subtly drift and undulate with pointer movement.
 * - High accessibility: Extremely soft opacity (0.15 - 0.22), pointer-events: none,
 *   full WCAG 2.3.3 reduced-motion compliance and automatic off-screen pausing.
 */
export const BreathRibbons = ({
    opacity = 0.18,
    sporeCount = 14,
    sx = {},
}) => {
    const containerRef = useRef(null);
    const canvasRef = useRef(null);
    const pointerRef = useRef({x: 0, y: 0, targetX: 0, targetY: 0});

    const [reducedMotion, setReducedMotion] = useState(() => {
        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
            return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
        }
        return false;
    });

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        if (!mediaQuery) return;
        const handleChange = (e) => setReducedMotion(Boolean(e?.matches));
        mediaQuery.addEventListener?.("change", handleChange);
        return () => mediaQuery.removeEventListener?.("change", handleChange);
    }, []);

    // Pointer listener for gentle air flow deflection
    useEffect(() => {
        if (reducedMotion || typeof window === "undefined") return;

        const handlePointerMove = (e) => {
            const width = window.innerWidth || 1000;
            const height = window.innerHeight || 800;
            pointerRef.current.targetX = (e.clientX / width) * 2 - 1;
            pointerRef.current.targetY = -(e.clientY / height) * 2 + 1;
        };

        window.addEventListener("pointermove", handlePointerMove, {passive: true});
        return () => window.removeEventListener("pointermove", handlePointerMove);
    }, [reducedMotion]);

    useEffect(() => {
        if (reducedMotion || !containerRef.current || !canvasRef.current) return;

        let renderer, scene, camera, ribbonsGroup, sporeMesh, ribbonTexture, sporeTexture;
        let animationFrameId = null;
        let isVisible = true;
        const clock = new THREE.Clock();
        const disposedGeos = [];
        const disposedMats = [];

        try {
            renderer = new THREE.WebGLRenderer({
                canvas: canvasRef.current,
                alpha: true,
                antialias: true,
                powerPreference: "low-power",
            });
        } catch {
            return;
        }

        const updateSize = () => {
            if (!containerRef.current || !renderer) return;
            const width = containerRef.current.clientWidth || 300;
            const height = containerRef.current.clientHeight || 200;
            renderer.setSize(width, height);
            renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.5));
            if (camera) {
                camera.aspect = width / height;
                camera.updateProjectionMatrix?.();
            }
        };

        scene = new THREE.Scene();
        camera = new THREE.PerspectiveCamera(48, 1, 0.1, 50);
        camera.position.set(0, 0, 9);
        camera.lookAt?.(0, 0, 0);

        updateSize();

        // 1. Procedural soft feather gradient texture for ribbons
        if (typeof document !== "undefined") {
            const rCanvas = document.createElement("canvas");
            rCanvas.width = 16;
            rCanvas.height = 64;
            const rCtx = rCanvas.getContext?.("2d");
            if (rCtx) {
                const grad = rCtx.createLinearGradient(0, 0, 0, 64);
                grad.addColorStop(0, "rgba(255, 255, 255, 0)");
                grad.addColorStop(0.2, "rgba(255, 255, 255, 0.55)");
                grad.addColorStop(0.5, "rgba(255, 255, 255, 1.0)");
                grad.addColorStop(0.8, "rgba(255, 255, 255, 0.55)");
                grad.addColorStop(1, "rgba(255, 255, 255, 0)");
                rCtx.fillStyle = grad;
                rCtx.fillRect(0, 0, 16, 64);
                if (THREE.CanvasTexture) {
                    ribbonTexture = new THREE.CanvasTexture(rCanvas);
                }
            }
        }

        ribbonsGroup = new THREE.Group ? new THREE.Group() : new THREE.Mesh();
        scene.add(ribbonsGroup);

        // 2. Define 3 flowing ribbons with individual breath rhythms and forest tones
        const ribbonDefs = [
            // Upper stream: airy sage green
            {color: 0x4a7a5a, baseY: 1.2, baseZ: -1.5, height: 2.2, freq: 0.28, speed: 0.35, amp: 0.55, phase: 0.0},
            // Center stream: warm morning mist
            {color: 0x6e967a, baseY: -0.3, baseZ: 0.2, height: 2.8, freq: 0.22, speed: 0.28, amp: 0.65, phase: 2.1},
            // Lower stream: deep forest green with golden warmth
            {color: 0x3d664c, baseY: -1.6, baseZ: -0.8, height: 2.4, freq: 0.32, speed: 0.32, amp: 0.5, phase: 4.2},
        ];

        const ribbonMeshes = [];
        const cols = 42;
        const rows = 4;

        ribbonDefs.forEach((def) => {
            let geo;
            if (THREE.PlaneGeometry) {
                geo = new THREE.PlaneGeometry(26, def.height, cols - 1, rows - 1);
            } else {
                geo = new THREE.BufferGeometry();
            }
            disposedGeos.push(geo);

            const matClass = THREE.MeshBasicMaterial;
            const mat = new matClass({
                color: def.color,
                transparent: true,
                opacity: opacity,
                map: ribbonTexture,
                side: THREE.DoubleSide || 2,
                depthWrite: false,
            });
            disposedMats.push(mat);

            const mesh = new THREE.Mesh(geo, mat);
            mesh.position.set(0, def.baseY, def.baseZ);
            ribbonsGroup.add(mesh);

            // Store original coordinates
            let origY = null;
            let origZ = null;
            const posAttr = geo.attributes?.position;
            if (posAttr && posAttr.array) {
                const count = posAttr.count || posAttr.array.length / 3;
                origY = new Float32Array(count);
                origZ = new Float32Array(count);
                for (let i = 0; i < count; i++) {
                    origY[i] = posAttr.array[i * 3 + 1];
                    origZ[i] = posAttr.array[i * 3 + 2];
                }
            }

            ribbonMeshes.push({
                mesh,
                def,
                geo,
                origY,
                origZ,
            });
        });

        // 3. Subtle floating light spores drifting around the ribbons
        let sporePositions, sporeSpeeds;
        if (sporeCount > 0 && THREE.BufferGeometry && THREE.PointsMaterial && THREE.Points) {
            const sporeGeo = new THREE.BufferGeometry();
            disposedGeos.push(sporeGeo);

            sporePositions = new Float32Array(sporeCount * 3);
            sporeSpeeds = new Float32Array(sporeCount);

            for (let i = 0; i < sporeCount; i++) {
                sporePositions[i * 3] = (Math.random() - 0.5) * 20;
                sporePositions[i * 3 + 1] = (Math.random() - 0.5) * 8;
                sporePositions[i * 3 + 2] = (Math.random() - 0.5) * 6;
                sporeSpeeds[i] = 0.005 + Math.random() * 0.01;
            }

            if (THREE.BufferAttribute) {
                sporeGeo.setAttribute(
                    "position",
                    new THREE.BufferAttribute(sporePositions, 3)
                );
            }

            // Procedural bokeh texture for spores
            if (typeof document !== "undefined") {
                const sCanvas = document.createElement("canvas");
                sCanvas.width = 32;
                sCanvas.height = 32;
                const sCtx = sCanvas.getContext?.("2d");
                if (sCtx) {
                    const g = sCtx.createRadialGradient(16, 16, 0, 16, 16, 16);
                    g.addColorStop(0, "rgba(255, 255, 255, 0.9)");
                    g.addColorStop(0.4, "rgba(215, 235, 220, 0.45)");
                    g.addColorStop(1, "rgba(215, 235, 220, 0)");
                    sCtx.fillStyle = g;
                    sCtx.fillRect(0, 0, 32, 32);
                    if (THREE.CanvasTexture) {
                        sporeTexture = new THREE.CanvasTexture(sCanvas);
                    }
                }
            }

            const sporeMat = new THREE.PointsMaterial({
                size: 0.32,
                color: 0x5a7d65,
                transparent: true,
                opacity: 0.35,
                map: sporeTexture,
                depthWrite: false,
                blending: 2,
            });
            disposedMats.push(sporeMat);

            sporeMesh = new THREE.Points(sporeGeo, sporeMat);
            scene.add(sporeMesh);
        }

        // Resize & Visibility Observers
        let resizeObserver = null;
        if (typeof ResizeObserver !== "undefined" && containerRef.current) {
            resizeObserver = new ResizeObserver(updateSize);
            resizeObserver.observe(containerRef.current);
        }

        let intersectionObserver = null;
        if (typeof IntersectionObserver !== "undefined" && containerRef.current) {
            intersectionObserver = new IntersectionObserver(
                ([entry]) => {
                    isVisible = entry.isIntersecting;
                },
                {threshold: 0.05}
            );
            intersectionObserver.observe(containerRef.current);
        }

        const animate = () => {
            animationFrameId = requestAnimationFrame(animate);
            if (!isVisible) return;

            const elapsed = clock.getElapsedTime();

            // Smooth pointer damping
            const p = pointerRef.current;
            p.x += (p.targetX - p.x) * 0.04;
            p.y += (p.targetY - p.y) * 0.04;

            // 8-second mindful breathing factor (4s inhale / 4s exhale)
            const breath = 1.0 + Math.sin(elapsed * 0.785) * 0.22;

            // Animate ribbons with harmonic waves
            ribbonMeshes.forEach((item) => {
                const pos = item.geo.attributes?.position;
                if (pos && pos.array && item.origY) {
                    const arr = pos.array;
                    const len = item.origY.length;
                    const def = item.def;

                    for (let i = 0; i < len; i++) {
                        const vx = arr[i * 3];

                        // Fluid sine-wave along ribbon length
                        const waveY = Math.sin(vx * def.freq + elapsed * def.speed + def.phase) * (def.amp * breath);
                        const waveZ = Math.cos(vx * def.freq * 0.8 + elapsed * def.speed * 0.7 + def.phase) * 0.4;

                        // Soft mouse deflection
                        const pointerDist = Math.hypot(vx - p.x * 8, item.origY[i] - p.y * 3);
                        const mouseBreeze = Math.sin(pointerDist * 0.8 - elapsed * 1.2) * Math.exp(-pointerDist * 0.25) * 0.2;

                        arr[i * 3 + 1] = item.origY[i] + waveY + mouseBreeze;
                        arr[i * 3 + 2] = item.origZ[i] + waveZ;
                    }
                    pos.needsUpdate = true;
                }
            });

            // Gentle camera parallax with mouse
            camera.position.x = p.x * 0.4;
            camera.position.y = p.y * 0.2;
            camera.lookAt?.(0, 0, 0);

            // Animate spores drifting upward
            if (sporeMesh && sporePositions) {
                const pos = sporeMesh.geometry?.attributes?.position;
                if (pos && pos.array) {
                    for (let i = 0; i < sporeCount; i++) {
                        pos.array[i * 3 + 1] += sporeSpeeds[i];
                        pos.array[i * 3] += Math.sin(elapsed * 0.4 + i) * 0.003;
                        if (pos.array[i * 3 + 1] > 4.5) {
                            pos.array[i * 3 + 1] = -4.5;
                            pos.array[i * 3] = (Math.random() - 0.5) * 20;
                        }
                    }
                    pos.needsUpdate = true;
                }
            }

            renderer.render(scene, camera);
        };

        animate();

        return () => {
            if (animationFrameId) cancelAnimationFrame(animationFrameId);
            if (resizeObserver) resizeObserver.disconnect();
            if (intersectionObserver) intersectionObserver.disconnect();
            disposedGeos.forEach((g) => g.dispose?.());
            disposedMats.forEach((m) => m.dispose?.());
            ribbonTexture?.dispose?.();
            sporeTexture?.dispose?.();
            renderer?.dispose?.();
            renderer?.forceContextLoss?.();
        };
    }, [reducedMotion, opacity, sporeCount]);

    return (
        <Box
            ref={containerRef}
            aria-hidden="true"
            sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                pointerEvents: "none",
                overflow: "hidden",
                zIndex: 0,
                ...sx,
            }}
        >
            {!reducedMotion && (
                <canvas
                    ref={canvasRef}
                    style={{
                        width: "100%",
                        height: "100%",
                        display: "block",
                        pointerEvents: "none",
                    }}
                />
            )}
        </Box>
    );
};
